import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { PermissionDef, UserRole } from '@redawn/shared-types';

import { apiFetch } from '../lib/api.ts';

/**
 * Role → permission matrix editor.
 *
 * `superadmin` is deliberately absent as a column: it bypasses the matrix in
 * code, so rendering a checkbox would imply it can be revoked — and revoking it
 * is precisely the state that would lock everyone out of this page.
 */
const ROLES: UserRole[] = [
  'admin',
  'director',
  'finance_manager',
  'manager',
  'operation',
  'operator',
  'logistics',
  'viewer',
];

interface MatrixResp {
  catalogue: PermissionDef[];
  matrix: Record<string, UserRole[]>;
}

export function AdminPermissionsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: () => apiFetch<MatrixResp>('/api/admin/permissions'),
  });

  const save = useMutation({
    mutationFn: ({ key, roles }: { key: string; roles: UserRole[] }) =>
      apiFetch('/api/admin/permissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, roles }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-permissions'] });
      // The signed-in user's own effective permissions may have just changed.
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  if (isLoading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error">{(error as Error).message}</div>;
  if (!data) return null;

  const groups = [...new Set(data.catalogue.map((p) => p.module))];

  function toggle(key: string, role: UserRole, on: boolean) {
    const current = data!.matrix[key] ?? [];
    const next = on ? [...new Set([...current, role])] : current.filter((r) => r !== role);
    save.mutate({ key, roles: next });
  }

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Role access</h1>
        <span className="main-subtitle">
          Which roles can reach each module. Changes apply immediately.
        </span>
      </header>

      <p style={{ color: 'var(--text-muted)', maxWidth: '70ch' }}>
        <strong>Superadmin</strong> always has every permission and isn’t shown — that’s the
        safeguard that stops this page locking everyone out. A user with several roles gets the
        union of what those roles allow.
      </p>

      {save.isError && <div className="error">{(save.error as Error).message}</div>}

      {groups.map((group) => (
        <section key={group} style={{ marginTop: 24 }}>
          <div className="sidebar-section" style={{ paddingLeft: 0 }}>{group}</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 260 }}>Function</th>
                  {ROLES.map((r) => (
                    <th key={r} className="mono" style={{ textAlign: 'center', fontSize: '0.8em' }}>{r}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.catalogue.filter((p) => p.module === group).map((p) => (
                  <tr key={p.key}>
                    <td>
                      <div>{p.label}</div>
                      <div className="mono" style={{ fontSize: '0.75em', color: 'var(--text-muted)' }}>{p.key}</div>
                      {p.note && (
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginTop: 2 }}>{p.note}</div>
                      )}
                    </td>
                    {ROLES.map((r) => {
                      const on = (data.matrix[p.key] ?? []).includes(r);
                      return (
                        <td key={r} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={save.isPending}
                            onChange={(e) => toggle(p.key, r, e.target.checked)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}
